import React from "react"
import { FormattedDate, FormattedMessage, injectIntl } from "react-intl"
import { useQuery } from "react-query"
import { RouteComponentProps } from "react-router"
import GlobalHelper from "../../common/GlobalHelper"
import Icon from "../../common/icon/icon.component.jsx"
import InlineEditDate from "../../common/inline-edit/date.component.jsx"
import InlineEditUser from "../../common/inline-edit/user.component"
import SideBar from "../../common/layout/sidebar.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import ContactLink from "../../quote/project/component/ContactLink"
import EmailLink from "../../quote/project/component/EmailLink"
import Proposal from "../../state/proposal/Proposal"
import ProposalService from "../../state/proposal/proposalService"
import AccountStatementAnalytics from "./AccountStatementAnalytics"
import AccountStatement from "./model/AccountStatement"
import AccountStatementService from "./service/AccountStatementService"



type Params = {
    accountStatementId: string,
}


const AccountStatementDetails = (props: RouteComponentProps<Params>) => {

    SideBar.activateMenu("")
    const accountStatementId = props.match.params.accountStatementId

    const renderFieldColumn = (colClass: string, labelFormattedMessage: FormattedMessage, mainValue: JSX.Element) => {
        return <div className={"position-relative mt-2 px-1 text-break " + colClass}>
            <div className={"py-1 px-2 h-100 inline-input"}>
                <div>
                    <label className="col-form-label custom-form-label project-details-label pt-0">
                        {labelFormattedMessage}
                    </label>
                </div>
                <div>
                    {mainValue}
                </div>
            </div>
        </div>
    }

    const { data: accountStatementData,
        isLoading,
        isError,
        refetch
    } = useQuery(["getAccountStatementAndProposal", accountStatementId], async () => {
        const accountStatement = await AccountStatementService.get(accountStatementId)
        if (accountStatement instanceof AccountStatement) {
            const proposal = await ProposalService.getProposal(accountStatement.getProposalHash())
            if (proposal instanceof Proposal) {
                const proposalEmails = await ProposalService.getProposalEmails(proposal.getId())
                return { accountStatement, proposal, proposalEmails }
            }
        }
    })
    if (isLoading || !accountStatementData) {
        return <LoadingSpinner className="py-2" />
    }

    const getSentAt = () => {
        if (accountStatementData.proposalEmails &&
            Array.isArray(accountStatementData.proposalEmails) &&
            accountStatementData.proposalEmails.length > 0) {
            const email = accountStatementData.proposalEmails.slice(-1)[0]
            return email.dto.attributes.sentAt
        }
        return null
    }

    return <>
        <div className={"py-3 mb-5"}>
            <div className={"d-flex flex-wrap ui-module"}>
                <div className={"col-lg-6 col-md-12 pl-md-3 pr-md-0 pr-lg-3 " +
                    (["lg", "xl"].includes(GlobalHelper.getResponsiveBreakpoint()) ? "border-right" : "")} >
                    <div className={"row mb-md-3"}>
                        <div className="col-12 pl-0 pr-md-0 pl-lg-1">
                            <h3 className={"m-0 d-flex align-items-center"}>
                                < FormattedMessage
                                    id="account-statement.analytics.title_label"
                                    defaultMessage="Account Statement {accountStatementId}"
                                    values={{
                                        accountStatementId: accountStatementData.accountStatement.getIdentifier()
                                    }}
                                />
                            </h3>
                        </div>
                        {renderFieldColumn("col-lg-3", <FormattedMessage
                            id="account-statement.details.statement-date"
                            defaultMessage="Statement Date"
                        />, <InlineEditDate
                            popupClassName={"top-inherit"}
                            value={accountStatementData.accountStatement.getDate()}
                            endPoint={"/account-statements/" + accountStatementData.accountStatement.getId()}
                            field={"date"}
                            editable={true}
                        />
                        )}
                        {renderFieldColumn("col-lg-3", <FormattedMessage
                            id="account-statement.details.statement-creation-date"
                            defaultMessage="Creation Date"
                        />, <FormattedDate
                            year='numeric'
                            month='long'
                            day='numeric'
                            value={accountStatementData.accountStatement.getCreatedAt()} />
                        )}
                        {getSentAt() && renderFieldColumn("col-lg-3", <FormattedMessage
                            id="account-statement.details.statement-sent-date"
                            defaultMessage="Sent Date"
                        />, <FormattedDate
                            year='numeric'
                            month='long'
                            day='numeric'
                            value={getSentAt()} />
                        )}
                    </div>
                </div>
                <div className={"col-lg-6 col-md-12 pl-md-3 pr-md-0 pr-lg-3 "} >
                    <div className={"row mb-md-3"}>
                        <div className="flex-grow-1 w-50 mw-50 pl-lg-2 pl-md-2">
                            <ContactLink contact={accountStatementData.accountStatement.getContact()} />
                            <EmailLink contact={accountStatementData.accountStatement.getContact()} />
                        </div>
                        <div className="flex-grow-1 text-right w-50 mw-50 pr-lg-0 pr-md-2">
                            <label className="col-form-label custom-form-label project-details-label pt-0">
                                <strong>
                                    <FormattedMessage
                                        id="quote.analytics.assign_to_label"
                                        defaultMessage="Assigned to"
                                    />
                                </strong>
                            </label>
                            <div className="m-0 position-relative">
                                <Icon value={"user"} />
                                <InlineEditUser
                                    editable={true}
                                    endPoint={"/account-statements/" + accountStatementData.accountStatement.getId()}
                                    field={"userId"}
                                    value={accountStatementData.accountStatement.getUser().getAttributes().name ?? ""}
                                    userId={accountStatementData.accountStatement.getUserId() ?? ""}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AccountStatementAnalytics
                accountStatement={accountStatementData.accountStatement}
                proposal={accountStatementData.proposal}
                proposalEmails={accountStatementData.proposalEmails}
                refetchEmails={refetch}
                isError={isError}
                isLoading={isLoading} />
        </div>
    </>
}

export default injectIntl(AccountStatementDetails)


// WEBPACK FOOTER //
// ./src/customer/account-statement/AccountStatementDetails.tsx